<template>
    <div v-if="local_bulk_message">
        <!-- Warnings & Alerts -->
        <rc-alert-local-storage
            icon="mdi-hammer-wrench"
            title="Excuse our dust!"
            message="We're working to improve your experience when creating a bulk message. The interface will change in the coming weeks, but all features should remain available. Thanks for your patience!"
            storage_key="rc-bulk-message-under-construction"
            class="mb-2"
        />

        <template>
            <v-alert
                border="left"
                color="blue"
                elevation="2"
                colored-border
            >
                <div class="d-flex">
                    <div style="flex-grow: 1">
                        <v-chip
                            dense
                            color="blue"
                            style="color: white;"
                            label
                        >
                            New
                        </v-chip>

                        Optimize your message's recipients using RedChirp's AI recipient list optimization!
                    </div>
                    <div style="flex-grow: 0">
                        <v-btn @click="onClickNewAIDetails">
                            Details...
                        </v-btn>
                    </div>
                </div>
            </v-alert>
        </template>

        <m-help-banner
            label="Need Help Creating or Sending a Bulk Message?  Click Here for Help!"
            url="https://redchirp.com/?p=7167"
        />

        <rc-info-dialog
            v-model="show_explainer_dialog"
            :persistent="false"
            icon=""
        >
            <template #title>
                <strong>New:</strong>&nbsp;<span class="font-weight-regular">Review Before You Send</span>
            </template>

            <template #default>
                <div class="text-body-1 mb-4">
                    Bulk Messages now include a <strong>Review step</strong>. You'll still build your message, select recipients, and apply filters here—just like before.
                    Then continue to the Review step, where you can preview your message, see who will receive it, and understand who's filtered out before sending.
                </div>

                <div class="text-body-1">
                    <rc-link href="https://redchirp.com/new-bulk-message-review-step-see-who-youll-reach-before-you-send/">
                        View the tutorial & video
                    </rc-link> to learn more about the updated workflow.
                </div>
            </template>

            <template #actions>
                <v-spacer />

                <v-btn
                    color="primary"
                    @click="doCloseExplainerDialog"
                >
                    Got It!
                </v-btn>
            </template>
        </rc-info-dialog>

        <!-- Form -->
        <v-form class="mb-8">
            <div class="mt-4 mb-2 d-flex align-center">
                <h2>Settings</h2>

                <v-menu
                    class="ml-2"
                    v-model="advanced_settings_menu"
                    :close-on-content-click="false"
                    offset-y
                >
                    <template #activator="{ on, attrs }">
                        <v-btn
                            icon
                            v-bind="attrs"
                            v-on="on"
                        >
                            <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                    </template>

                    <v-card
                        class="pa-4"
                        style="min-width: 300px;"
                    >
                        <v-card-title class="px-0 pt-0 pb-1">
                            Advanced Settings
                        </v-card-title>
                        <v-checkbox
                            v-model="local_bulk_message.is_double_opt_in_message"
                            label="Bulk message should start a double opt-in process"
                            :disabled="has_sent"
                            @change="upsertBulkMessage"
                        />
                    </v-card>
                </v-menu>
            </div>

            <m-edit-text
                label="Bulk Message Name"
                :value="local_bulk_message.label"
                @input="updateBulkMessageLabel"
            />

            <m-edit-tags
                label="Tags"
                applies_to="bulk-message"
                class="mt-n4"
                v-model="local_bulk_message.rc_tag_ids"
                @input="upsertBulkMessage"
            />

            <div data-validation-target="from_endpoint">
                <m-edit-select-organization-owned-endpoint
                    :organization_id="local_bulk_message.organization_id"
                    :value="local_bulk_message.from_endpoint"
                    :disabled="has_sent"
                    @input="selectTeamEndpoint"
                />
            </div>

            <div data-validation-target="category">
                <v-select
                    label="Message Type"
                    :items="message_type_choices"
                    v-model="local_bulk_message.category"
                    :disabled="has_sent"
                    @change="updateBulkMessageType"
                />
            </div>

            <v-alert
                v-if="local_bulk_message.is_double_opt_in_message"
                type="warning"
                class="mb-4"
                outlined
            >
                <strong>Disclaimer</strong><br />
                <p>This is not legal advice. Consult an Attorney if you have legal or compliance questions about your
                    text marketing campaigns.</p>
                <p>RedChirp provides users with a template message that might be used to obtain affirmative written
                    consent
                    for marketing and promotional messages from individuals on your contact list. As with any marketing
                    campaign, you should consider the target audience, risks of reassigned or incorrect numbers, recent
                    interactions with the customer, and what, if any, existing consent you already have from your
                    customer base.
                    Keep in mind that as time passes since the customer provided you their number there's increased risk
                    that
                    the number no longer belongs to the person that provided it.</p>
                <p>We recommend against adding any promotional, incentive, or marketing language to this message. When
                    in
                    doubt, complicated legal questions should be raised with an attorney experienced in telemarketing
                    compliance.</p>
            </v-alert>

            <div data-validation-target="old_message_text">
                <m-edit-textarea
                    v-if="use_old_message_patterns"
                    label="Message Text"
                    :value="old_message_text"
                    :optional="false"
                    :hint="text_message_hint_text"
                    :rules="[rules.no_pay_links]"
                    :read_only="has_sent"
                    :force_mms="message_type_force_mms"
                    :insert_chips="message_variables"
                    use_compose_message
                    show_message_type
                    @input="updateOldBulkMessageText"
                />
            </div>

            <div data-validation-target="message_patterns">
                <m-edit-message-patterns-compact
                    v-if="!use_old_message_patterns"
                    v-model="local_bulk_message.message_patterns"
                    label="Message"
                    context="bulk-message"
                    class="mb-4 pb-2"
                    style="border-bottom: 1px solid rgb(148,148,148);"
                    :bulk_message_id="local_bulk_message.bulk_message_id"
                    :disabled="has_sent"
                    :always_mms="message_type_force_mms || local_bulk_message.vcard_id != null"
                    @example-message-changed="$emit('example-message-changed', $event)"
                    @input="upsertBulkMessage"
                />
            </div>

            <v-select
                v-if="show_shorten_links_using"
                label="Shorten Links Using"
                :items="link_shortener_host_choices"
                v-model="local_bulk_message.link_shortener_host"
                :readonly="has_sent"
                :disabled="has_sent"
                @change="upsertBulkMessage"
            />

            <div class="mb-4 text-right">
                <v-menu
                    bottom
                    offset-y
                >
                    <template #activator="{ on }">
                        <v-btn
                            class="mx-2"
                            fab
                            dark
                            small
                            color="primary"
                            :disabled="has_sent"
                            v-on="on"
                        >
                            <v-icon dark>mdi-dots-vertical</v-icon>
                        </v-btn>
                    </template>

                    <v-list>
                        <v-list-item @click="onClickChangeVariableStyle">
                            <v-list-item-title>
                                {{ use_old_message_patterns
                                    ? 'Use new style variables (e.g. {first-name})'
                                    : 'Use legacy variables (e.g. %first_name%)'
                                }}
                            </v-list-item-title>
                        </v-list-item>

                        <v-divider />

                        <v-list-item @click="onClickTemplates">
                            <v-list-item-title>Templates...</v-list-item-title>
                        </v-list-item>

                        <v-divider v-if="feature_available_rc_ai_improve_content" />

                        <m-restricted-menu-list-item
                            v-if="feature_available_rc_ai_improve_content"
                            text="RedChirp AI: Suggestions to improve message content..."
                            @click="onClickRedChirpAIImproveContent"
                        >
                            <m-menu-chip />
                        </m-restricted-menu-list-item>
                    </v-list>
                </v-menu>

                <m-model-r1-suggestions-to-improve-message-content-dialog
                    ref="m-model-r1-suggestions-to-improve-message-content-dialog"
                    @update="update"
                />
            </div>

            <m-edit-blobs
                label="Image (optional)"
                empty_text="No image"
                v-model="local_bulk_message.blob_ids"
                :allow_multiple="false"
                :limit_to_size="460800"
                :read_only="has_sent"
                class="my-4 pb-2"
                style="border-bottom: 1px solid rgb(148,148,148);"
                edit_icon="mdi-lead-pencil"
                dialog_title="Bulk Message Image"
                dialog_hint="<b>Image limitations:</b>  Images <b>must</b> be 450 Kb or smaller.<br/><br/><b>Image size:  </b>For best results, images should be square or close to square.  The ideal image size is 400 x 400 pixels."
                process_media
                externally_visible
                @input="onChangeBlobs"
            />

            <m-select-vcard
                label="Attach contact card (optional)"
                v-model="local_bulk_message.vcard_id"
                ref="select-contact-card"
                class="mt-4"
                show_bulk_message_warning
                :read_only="has_sent"
                @input="upsertBulkMessage"
            />

            <v-chip
                color="primary"
                class="ma-2"
                label
                small
            >
                <strong>
                    {{ message_type }}
                </strong>
            </v-chip>

            <v-select
                label="Outcome Tracking"
                class="mt-4"
                :items="outcome_tracking_items"
                :value="use_outcome_tracking"
                @change="updateOutcomeTracking"
            />

            <div v-if="use_outcome_tracking === 'yes'">
                <m-edit-text
                    v-for="(item, index) in outcomes"
                    :key="index"
                    :label="'Outcome #' + (index + 1) + ' Name'"
                    :value="item.label"
                    :menu_items="outcome_menu_items"
                    @input="$emit('after-edit-outcome', $event, item)"
                    @delete="$emit('after-delete-outcome', item)"
                />

                <m-icon-bar @add="$emit('create-outcome')" />
            </div>

            <div
                data-validation-target="recipients"
                class="d-flex align-center gap-2"
            >
                <h2>Recipients</h2>
                <span
                    v-if="!full_text && num_recipients_under_current_filter"
                    class="text-caption text--secondary d-inline-block"
                >
                    ({{ num_recipients_under_current_filter.toLocaleString() }})
                </span>
            </div>

            <recipients-table-edit
                :organization_id="organization_id"
                :bulk_message="bulk_message"
                :bulk_message_id="bulk_message_id"
                :loading_recipients="loading_recipients"
                :recipients="recipients"
                :recipient_extra_fields="recipient_extra_fields"
                :full_text="full_text"
                :num_recipients_under_current_filter="num_recipients_under_current_filter"
                :table_options.sync="local_table_options"
                @update="update"
                @split-message="$emit('split-message')"
                @select-test-message-recipient="$emit('select-test-message-recipient', $event)"
                @delete-recipient="$emit('delete-recipient', $event)"
                @update-full-text="$emit('update-full-text', $event)"
                @update-recipients="$emit('update-recipients')"
                @export-recipients-to-excel="$emit('export-recipients-to-excel')"
                @edit-error-rate-before-cancel="onEditErrorRateBeforeCancel"
                @preview-message="previewMessage"
                @preview-bulk-recipients="onPreviewBulkRecipients"
                @recipient-validation="$emit('recipient-validation')"
                @view-imports="$emit('view-imports')"
            />

            <m-edit-bulk-message-filters
                :bulk_message="local_bulk_message"
                :has_sent="has_sent"
                @patch="onPatchFilter"
            />
        </v-form>

        <!-- CTA -->
        <div v-show="local_bulk_message.scheduled_send_time == null && !local_bulk_message.is_archived">
            <div class="mt-12">
                <div class="d-flex align-center gap-2 mb-2">
                    <h2>Next Step: Review Your Message Before Sending</h2>

                    <!-- TODO: expiration for being "new" -->
                    <v-chip
                        color="primary"
                        dense
                        label
                        small
                    >
                        <strong>
                            New
                        </strong>
                    </v-chip>
                </div>

                <v-row>
                    <v-col
                        cols="12"
                        md="10"
                        lg="8"
                        xl="7"
                    >
                        <v-alert
                            border="left"
                            color="primary"
                            elevation="0"
                            class="shaded-light pl-5 mt-1"
                            colored-border
                            dense
                        >
                            <p>
                                By clicking Continue, you'll go to a review screen where you can check the message
                                details and recipients before sending.

                            </p>
                            <p class="mb-0">
                                Your message will not be sent until you finish reviewing and
                                approve it.
                            </p>
                        </v-alert>
                    </v-col>
                </v-row>
            </div>

            <v-row
                v-if="shouldDisable()"
                class="mt-2 mb-1"
            >
                <v-col cols="12">
                    <step-validation
                        :reasons="reasonsPreventingNextStep()"
                        class="mb-4"
                    />
                </v-col>
            </v-row>

            <v-btn
                :disabled="shouldDisable()"
                color="primary"
                class="mr-4 mb-12"
                :class="{ 'mt-2': !shouldDisable() }"
                @click="onClickNextStage"
            >
                Continue
                <v-icon
                    aria-hidden="true"
                    right
                >
                    mdi-arrow-right
                </v-icon>
            </v-btn>
        </div>

        <!-- Dialogs -->
        <m-bulk-message-template-pick-dialog
            ref="bulk-message-template-pick-dialog"
            @ok="onUseBulkMessageTemplate"
        />

        <m-bulk-recipient-preview-message-dialog ref="preview-message-dialog" />

        <m-bulk-message-attachment-warning-dialog ref="bulk-message-attachment-warning-dialog" />

        <m-preview-bulk-recipients-dialog
            ref="preview-bulk-recipients"
            @ok="afterCommittingExcelChanges"
        />
    </div>
</template>

<script>
import _ from 'lodash';
import { MESSAGE_STATES } from '@/js/bulk/bulk-states.js';
import RecipientsTableEdit from '@/components/bulk/recipients-table-edit.vue';
import StepValidation from '../step-validation.vue';

export default {
    name: 'BulkMessageEditState',

    components: {
        RecipientsTableEdit,
        StepValidation,
    },

    props: {
        organization_id: {
            type: String,
            required: true,
        },
        bulk_message: {
            type: Object,
            required: false,
            default: null
        },
        bulk_message_id: {
            type: String,
            required: true,
        },
        loading_recipients: {
            type: Boolean,
            required: true,
        },
        recipients: {
            type: Array,
            required: true,
        },
        recipient_extra_fields: {
            type: Array,
            required: false,
            default: () => [],
        },
        num_recipients_under_current_filter: {
            type: Number,
            required: true,
        },
        use_outcome_tracking: {
            type: String,
            required: false,
            default: null
        },
        outcomes: {
            type: Array,
            required: false,
            default: () => []
        },
        has_sent: {
            type: Boolean,
            required: true,
        },
        use_old_message_patterns: {
            type: Boolean,
            required: true,
        },
        old_message_text: {
            type: String,
            required: false,
            default: null
        },
        message_type: {
            type: String,
            required: true,
        },
        message_type_force_mms: {
            type: Boolean,
            required: false,
            default: false
        },
        full_text: {
            type: String,
            required: false,
            default: '',
        },
        table_options: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            show_explainer_dialog: false,
            advanced_settings_menu: false,
            local_bulk_message: null,

            outcome_tracking_items: [
                { text: 'Do not use outcome tracking', value: 'no' },
                { text: 'Use outcome tracking', value: 'yes' },
            ],
            outcome_menu_items: [
                { text: 'Edit', event: 'edit' },
                { text: 'Delete', event: 'delete' }
            ],

            rules: {
                no_pay_links: (value) => {
                    const normalized_text = value.toLowerCase().trim();
                    if (normalized_text.indexOf('redchirp.com/app/pay/') >= 0) {
                        return 'Message cannot contain pay links: https://app.redchirp.com/app/pay';
                    }
                    return true;
                },
            },
            message_type_choices: [
                { text: 'Informational', value: 'informational' },
                { text: 'Promotional', value: 'promotional' }
            ],
        };
    },

    watch: {
        bulk_message: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    // local copy to avoid mutating prop directly
                    this.local_bulk_message = _.cloneDeep(newVal);
                }
            }
        },
    },

    computed: {
        local_table_options: {
            get() {
                return this.table_options;
            },
            set(value) {
                this.$emit('update:table_options', value);
            }
        },

        text_message_hint_text() {
            return 'This will be the text that the customer will receive when the bulk message is sent. %first_name%, %last_name%, ' +
                'and %phone_number% will be replaced with their appropriate values when the text message goes out.';
        },

        link_shortener_host_choices() {
            const ret = [
                { text: 'Do not shorten links', value: null }
            ];

            const hosts = this.$rc.getDeep(this.local_bulk_message, 'link_shortener_hosts');
            if (hosts == null) return ret;

            for (let i = 0; i < hosts.length; i++) {
                if (hosts[i].is_active == false) continue;
                ret.push({ text: hosts[i].host, value: hosts[i].host });
            }

            return ret;
        },

        show_shorten_links_using() {
            if (!this.local_bulk_message) return false;

            let text = this.old_message_text;
            if (text == null) text = '';

            text = text.toLowerCase();

            const has_link = text.includes('http://') || text.includes('https://');

            return this.local_bulk_message.provision_branded_link_shortener == true && has_link;
        },

        feature_available_rc_ai_improve_content() {
            return this.$rc.isAuthActorSystemAdmin();
        },

        message_variables() {
            const ret = [
                { text: 'First Name', value: '%first_name%' },
                { text: 'Last Name', value: '%last_name%' },
                { text: '%', value: '%%' },
            ];

            for (let i = 0; i < this.recipient_extra_fields.length; i++) {
                const cur = this.recipient_extra_fields[i];
                ret.push(
                    {
                        text: cur.extra_field_key,
                        value: '%' + cur.extra_field_key + '%',
                    }
                );
            }

            return ret;
        },

        bad_variables_in_message() {
            return this.listVariablesUsedInMessageThatDontExist();
        },
    },

    methods: {
        update() {
            this.$emit('update');
        },

        upsertBulkMessage() {
            this.$emit('upsert-bulk-message', this.local_bulk_message);
        },

        onClickNewAIDetails() {
            window.open('https://redchirp.com/introducing-redchirps-ai-recipient-list-optimization/', '_blank');
        },

        updateBulkMessageLabel(new_label) {
            this.local_bulk_message.label = new_label;
            this.upsertBulkMessage();
        },

        selectTeamEndpoint(endpoint) {
            this.local_bulk_message.from_endpoint = endpoint;
            this.upsertBulkMessage();
        },

        updateBulkMessageType(new_type) {
            this.local_bulk_message.type = new_type;
            this.upsertBulkMessage();
        },

        updateOldBulkMessageText(new_value) {
            this.local_bulk_message.message_patterns = [new_value.trim()];
            this.upsertBulkMessage();
        },

        onClickChangeVariableStyle() {
            if (this.use_old_message_patterns) {
                this.$rc.confirm({
                    title: 'Switch message to use new style variables',
                    body: 'Are you certain that you would like to switch over to use new style variables? (e.g. {first-name})',
                    btn_text_ok: 'Use new style variables',
                    btn_text_cancel: 'Cancel',
                    on_ok: () => {
                        this.local_bulk_message.message_patterns = [
                            'Hi {first-name}, this is {organization-label}.\n\nYour message goes here.\n\nText STOP to opt-out.'
                        ];
                        this.local_bulk_message.use_legacy_variables = false;
                        this.upsertBulkMessage();
                    },
                });
            }
            else {
                this.$rc.confirm({
                    title: 'Switch message to use old style variables',
                    body: 'Are you certain that you would like to switch over to use old style variables? (e.g. %first-name%)',
                    btn_text_ok: 'Use legacy variables',
                    btn_text_cancel: 'Cancel',
                    on_ok: () => {
                        this.local_bulk_message.message_patterns = [];
                        this.local_bulk_message.use_legacy_variables = true;
                        this.upsertBulkMessage();
                    },
                });
            }
        },

        onClickTemplates() {
            this.$refs['bulk-message-template-pick-dialog'].showDialog();
        },

        doShowExplainerDialog() {
            this.show_explainer_dialog = true;
            localStorage.setItem('rc-bulk-message-edit-step-explainer-shown', 'true');
        },

        doCloseExplainerDialog() {
            this.show_explainer_dialog = false;
        },

        onUseBulkMessageTemplate(template) {
            this.local_bulk_message.message_patterns = template.message_patterns;
            this.local_bulk_message.template_id = template.template_id;
            this.$rc.snackbar('Message has been updated: ' + template.label);
            this.upsertBulkMessage();
        },

        onClickRedChirpAIImproveContent() {
            this.$rc.startProcess({
                type: 'inference-model-r1-message-content',

                params: {
                    bulk_message_id: this.local_bulk_message.id,
                    force_refresh_inference: false,
                },

                title: 'RedChirp AI: Suggestions to Improve Message Content',
                finished_button_text: 'View Suggestions',
                failed_button_text: 'Ok',

                status: (pte) => {
                    switch (pte.state) {
                        case 'failed': return 'Unable to generate improvement suggestions';
                        case 'finished': return 'Suggestions generated';
                    }
                    return 'Generating message content improvement suggestions';
                },

                on_finished: (pte) => {
                    this.$refs['m-model-r1-suggestions-to-improve-message-content-dialog'].showDialog(pte.output);
                },
                on_failed: (pte) => {
                    this.update();
                },
            });
        },

        onChangeBlobs() {
            this.$rc.getBlobMetaDataMultiple(
                this.local_bulk_message.blob_ids,

                (data) => {
                    this.$rc.hideSpinner();

                    this.$refs['bulk-message-attachment-warning-dialog'].showDialog(
                        data.meta_data,
                        () => {
                            this.upsertBulkMessage();
                        },
                        () => {
                            this.local_bulk_message.blob_ids = [];
                            this.upsertBulkMessage();
                        },
                    );
                },

                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error', 'Unable to get blob meta data');
                }
            );
        },

        onEditErrorRateBeforeCancel() {
            this.$rc.confirm({
                title: 'Warning: Max Error Rate',
                body: 'Sending a bulk message with a high error rate could incur significant charges for messages that do not reach your recipients. Are you sure you want to edit this setting?',
                btn_text_ok: 'Adjust Error Rate',
                btn_text_cancel: 'Cancel',
                on_ok: () => {
                    this.$rc.prompt({
                        title: 'Max error rate setting',
                        label: 'If more than 100 messages have been sent and more than this percentage of recipients return an error, this bulk message will be automatically cancelled',
                        value: this.local_bulk_message.max_error_percent_before_cancel,
                        is_valid: this.$rc.validationRuleInteger(0, 100),
                        after_prompt: (value) => {
                            this.local_bulk_message.max_error_percent_before_cancel = value;
                            this.upsertBulkMessage();
                        }
                    });
                }
            });
        },

        updateOutcomeTracking(new_value) {
            switch (new_value) {
                case 'yes':
                    {
                        this.local_bulk_message.use_outcome_tracking = true;
                        break;
                    }
                case 'no':
                    {
                        this.local_bulk_message.use_outcome_tracking = false;
                        break;
                    }
            }

            this.upsertBulkMessage();
        },

        removeURLS(value) {
            let longestURL = (str) => {
                let words = str.split(" ");

                let ret = null;

                for (let i = 0; i < words.length; i++) {
                    let word = words[i];
                    if (word.toLowerCase().startsWith('http://') || word.toLowerCase().startsWith('https://')) {
                        if (ret == null || ret.length < word.length)
                            ret = word;
                    }
                }

                return ret;
            }

            while (value.length > 0) {
                let longest_url = longestURL(value);
                if (longest_url == null) break;
                value = value.replace(longest_url, '');
            }

            return value;
        },

        listVariablesUsedInMessageThatDontExist() {
            if (!this.local_bulk_message) return [];
            if (!this.use_old_message_patterns) return [];
            if (this.old_message_text == null) return [];

            let value = this.removeURLS(this.old_message_text);
            let ret = [];

            while (value.length > 0) {
                let start_idx = value.indexOf('%');
                if (start_idx == -1 || start_idx >= value.length - 1) break;

                value = value.substring(start_idx + 1);

                let end_idx = value.indexOf('%');

                if (end_idx == -1)
                    break;

                let cur_var = value.substring(0, end_idx);

                let found_var = false;

                for (let i = 0; i < this.message_variables.length; i++) {
                    if (this.message_variables[i].value.toLowerCase() == ('%' + cur_var + '%').toLowerCase())
                        found_var = true;
                }

                if (cur_var.length == 0)
                    found_var = true; // they empty variable (i.e. %% is used to encode  single % safely -- it exists, therefore)

                if (!found_var) {
                    ret.push('You are using an undefined variable, %' + cur_var + '%' + ', in your message');
                }

                value = value.substring(end_idx + 1);
            }

            return ret;
        },

        previewMessage(recipient) {
            if (this.use_old_message_patterns && this.listVariablesUsedInMessageThatDontExist().length > 0) {
                let message = '';
                let vars = this.listVariablesUsedInMessageThatDontExist();

                for (let i = 0; i < vars.length; i++) {
                    message += vars[i];
                    message += '<br/><br/>';
                }

                message += 'You will not be able to send or preview your message until you remove the undefined variables from your message';

                this.$rc.alert('Your message uses undefined variables', message);
                return;
            }

            this.$rc.rest({
                spinner: true,
                endpoint: 'PUT: /api/v1/bulk/messages/preview',

                params: {
                    bulk_message_id: this.bulk_message_id,
                    bulk_recipient_id: recipient.bulk_message_recipient_id,
                },

                on_success: (data) => {
                    this.$refs['preview-message-dialog'].showDialog(data.message, this.local_bulk_message.blob_ids, data.technical_details);
                },

                on_error: 'Unable to create preview message',
            });
        },

        onPatchFilter(patch) {
            this.$emit('patch', patch);
        },

        onPreviewBulkRecipients(recipients) {
            this.$refs['preview-bulk-recipients'].showDialog(
                this.organization_id,
                this.bulk_message_id,
                recipients
            );
        },

        afterCommittingExcelChanges() {
            this.$emit('update-recipients')
        },

        // VALIDATION
        allTemplatePlaceholders(message) {
            const messages = Array.isArray(message) ? message : [message];

            const ret = [];

            for (let i = 0; i < messages.length; i++) {
                let cur_message = messages[i];
                let m = cur_message.match(/{placeholder.+}/g);
                if (m == null) m = [];

                for (let j = 0; j < m.length; j++) {
                    if (!ret.includes(m[j]))
                        ret.push(m[j]);
                }
            }

            return ret;
        },

        shouldDisable() {
            return this.reasonsPreventingNextStep().length > 0;
        },

        reasonsPreventingNextStep() {
            const ret = [];

            if (this.$rc.$state.profile?.provision_info?.bulk_message_send_disabled_message != null) {
                ret.push(
                    {
                        text: 'Sending bulk messages has been disabled for your organization',
                        help_text: this.$rc.$state.profile?.provision_info?.bulk_message_send_disabled_message
                    }
                );
            }

            if (this.$rc.$state.profile.employee_account.bulk_message_permissions != 'send-bulk-messages') {
                ret.push(
                    {
                        text: 'Insufficient permissions to send bulk message',
                        help_text: 'To enable sending bulk messages, ask an admin of your organization to update your user permissions. These can be changed in Organization Settings > RedChirp Users > Action > Set Bulk Message Permissions.'
                    }
                );
            }

            if (this.local_bulk_message) {
                if (this.use_old_message_patterns && this.old_message_text == null) {
                    ret.push({ text: 'Message Text cannot be blank', target: 'old_message_text' });
                }

                if (this.local_bulk_message.from_endpoint == null || this.local_bulk_message.from_endpoint == '') {
                    ret.push({ text: 'Must select a phone number', target: 'from_endpoint' });
                }

                const message_patterns = this.local_bulk_message.message_patterns || [];
                const target = this.use_old_message_patterns ? 'old_message_text' : 'message_patterns';
                if (message_patterns.some(pattern => pattern.includes('Your message goes here'))) {
                    ret.push({ text: 'Message still has placeholder "Your message goes here" content', target });
                }
            }

            if (this.local_bulk_message.template_id != null) {
                const placeholders = this.allTemplatePlaceholders(this.local_bulk_message.message_patterns);
                placeholders.forEach((item) => ret.push({ text: 'Your message contains a placeholder variable: ' + item, target: 'message_patterns' }));
            }

            const is_mms = this.message_type.startsWith('MMS') || this.local_bulk_message.blob_ids.length > 0;
            const max_pattern_length = this.$rc.codePointLength(this.local_bulk_message.message_patterns.reduce((l, r) => l.length > r.length ? l : r, ''));

            if (is_mms && max_pattern_length > 1550) // MMS 1600, with 50 char for variable substitution buffer
            {
                ret.push(
                    {
                        text: 'Message text is too long (max 1550)',
                        //help_text : 'The MMS protocol has a maximum message length of 1600 characters. RedChirp reserves 50 characters to accommodate variable substitution during message generation.'
                        target: 'message_patterns',
                    }
                )
            }

            if (this.recipients == null || this.recipients.length == 0) {
                ret.push({ text: 'Needs to have at least one recipient', target: 'recipients' });
            } else {
                let has_at_least_one_unsent = false;
                for (let i = 0; i < this.recipients.length; i++) {
                    let recipient = this.recipients[i];

                    // if they haven't received a text, ensure they're not opted out
                    if (recipient.has_opted_out_of_bulk_text) {
                        continue;
                    }

                    if (recipient.sent_time == null || recipient.sent_time == 0) {
                        has_at_least_one_unsent = true;
                        break;
                    }
                }
                if (!has_at_least_one_unsent) {
                    ret.push({ 'text': 'Need at least one new recipient to send this message again', target: 'recipients' });
                }
            }


            for (let i = 0; i < this.bad_variables_in_message.length; i++) {
                ret.push({ 'text': this.bad_variables_in_message[i], target: 'old_message_text' });
            }

            //Check if message type is not informational and double opt-in is enabled
            if (this.local_bulk_message.is_double_opt_in_message && this.local_bulk_message.category !== 'informational') {
                ret.push({ text: 'Double opt-in messages should be sent with the informational message type', target: 'category' });
            }

            if (this.local_bulk_message.before_send_add_list_ids?.length > 0) {
                ret.push({ text: 'Clear all selections from "Before sending, create recipients... from list"', target: 'recipients' });
            }

            return ret;
        },

        onClickNextStage() {
            this.$emit('change-state', MESSAGE_STATES.PREPARE);
        },
    },

    mounted() {
        const explainer_shown = localStorage.getItem('rc-bulk-message-edit-step-explainer-shown');
        if (explainer_shown !== 'true') {
            this.doShowExplainerDialog();
        }
    }
};
</script>

<style lang="scss" scoped></style>